import { useEffect, useReducer, useState } from "react";

import useMaster from "hook/useMaster";
import { getRole } from "utils/routeUtility";
import { useApi } from "libs/data-access/src/api/useApi";
import { flattenObject, showToast } from "utils/utility";

import { ICommonMyChargeError, IMyCharges } from "./types";


type CancellationCharges = IMyCharges["details"]["cancellationCharges"];
type TransactionCharges = IMyCharges["details"]["transactionCharges"];
type ModificationCharges = IMyCharges["details"]["modificationCharges"];
type CreditCardOrIVRCharges = IMyCharges["details"]["creditCardOrIVRCharges"];

type ActionTypes =
    | {
        action: "UPDATE_SERVICE";
        payload: IMyCharges["service"];
    }
    | {
        action: "UPDATE_CANCELLATION_CHARGES";
        payload: {
            field: keyof CancellationCharges;
            value: CancellationCharges[keyof CancellationCharges];
        };
    }
    | {
        action: "SET_CANCELLATION_CHARGES";
        payload: CancellationCharges;
    }
    | {
        action: "UPDATE_CANCELLATION_CHARGE_ERROR";
        payload: {
            field: keyof ICommonMyChargeError;
            value: ICommonMyChargeError[keyof ICommonMyChargeError];
        };
    }
    | {
        action: "UPDATE_CANCELLATION_CHARGE_ERRORS";
        payload: ICommonMyChargeError;
    }
    | {
        action: "UPDATE_TRANSACTION_CHARGES";
        payload: {
            field: keyof TransactionCharges;
            value: TransactionCharges[keyof TransactionCharges];
        };
    }
    | {
        action: "SET_TRANSACTION_CHARGES";
        payload: TransactionCharges;
    }
    | {
        action: "UPDATE_TRANSACTION_CHARGE_ERROR";
        payload: {
            field: keyof ICommonMyChargeError;
            value: ICommonMyChargeError[keyof ICommonMyChargeError];
        };
    }
    | {
        action: "UPDATE_TRANSACTION_CHARGE_ERRORS";
        payload: ICommonMyChargeError;
    }
    | {
        action: "UPDATE_MODIFICATION_CHARGES";
        payload: {
            field: keyof ModificationCharges;
            value: ModificationCharges[keyof ModificationCharges];
        };
    }
    | {
        action: "SET_MODIFICATION_CHARGES";
        payload: ModificationCharges;
    }
    | {
        action: "UPDATE_MODIFICATION_CHARGE_ERROR";
        payload: {
            field: keyof ICommonMyChargeError;
            value: ICommonMyChargeError[keyof ICommonMyChargeError];
        };
    }
    | {
        action: "UPDATE_MODIFICATION_CHARGE_ERRORS";
        payload: ICommonMyChargeError;
    }
    | {
        action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGES";
        payload: {
            field: keyof CreditCardOrIVRCharges;
            value: CreditCardOrIVRCharges[keyof CreditCardOrIVRCharges];
        };
    }
    | {
        action: "SET_CREDIT_CARD_OR_IVR_CHARGES";
        payload: CreditCardOrIVRCharges;
    }
    | {
        action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGE_ERROR";
        payload: {
            field: keyof ICommonMyChargeError;
            value: ICommonMyChargeError[keyof ICommonMyChargeError];
        };
    }
    | {
        action: "UPDATE_CREDIT_CARD_OR_IVR_CHARGE_ERRORS";
        payload: ICommonMyChargeError;
    }
    | {
        action: "RESET_ALL_CHARGES";
    };

const useMyCharges = () => {

    const {
        serviceTypes,
        allFeeTypes,
        applyForTypes,
        bookingChargeCategories,
        getServiceTypes,
        getAllFeeTypes,
        getBookingChargeCategories,
    } = useMaster();

    const {
        getData,
        postData,
        putData,
        patchData
    } = useApi();


    const myChargesReducer = (state: IMyCharges, action: ActionTypes): IMyCharges => {
        switch (action.action) {
            case "UPDATE_SERVICE":
                return {
                    ...state,
                    service: action.payload,
                    details: {
                        cancellationCharges: {
                            affiliateDomesticCharge: 0,
                            affiliateInternationalCharge: 0,
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN"
                        },
                        transactionCharges: {
                            affiliateDomesticCharge: 0,
                            affiliateInternationalCharge: 0,
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN"
                        },
                        modificationCharges: {
                            affiliateDomesticCharge: 0,
                            affiliateInternationalCharge: 0,
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN"
                        },
                        creditCardOrIVRCharges: {
                            affiliateDomesticCharge: 0,
                            affiliateInternationalCharge: 0,
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN",
                            affiliateDomesticChargeApplyFor: "PER_BOOKING",
                            affiliateDomesticChargeType: "FIXED",
                            affiliateInternationalChargeApplyFor: "PER_BOOKING",
                            affiliateInternationalChargeType: "FIXED",
                            customerDomesticChargeApplyFor: "PER_BOOKING",
                            customerDomesticChargeType: "FIXED",
                            customerInternationalChargeApplyFor: "PER_BOOKING",
                            customerInternationalChargeType: "FIXED"
                        },
                    }
                };

            case "UPDATE_CANCELLATION_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        cancellationCharges: {
                            ...state.details.cancellationCharges,
                            [action.payload.field]: action.payload.value,
                        },
                    },
                    errors: {
                        ...state.errors,
                        cancellationCharges: {
                            ...state.errors.cancellationCharges,
                            [action.payload.field]: undefined,
                        },
                    }
                };
            case "SET_CANCELLATION_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        cancellationCharges: action.payload
                    }
                };
            case "UPDATE_CANCELLATION_CHARGE_ERROR":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        cancellationCharges: {
                            ...state.errors.cancellationCharges,
                            [action.payload.field]: action.payload.value,
                        }
                    }
                };
            case "UPDATE_CANCELLATION_CHARGE_ERRORS":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        cancellationCharges: action.payload
                    }
                };

            case "UPDATE_TRANSACTION_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        transactionCharges: {
                            ...state.details.transactionCharges,
                            [action.payload.field]: action.payload.value,
                        },
                    },
                    errors: {
                        ...state.errors,
                        transactionCharges: {
                            ...state.errors.transactionCharges,
                            [action.payload.field]: undefined,
                        },
                    }
                };
            case "SET_TRANSACTION_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        transactionCharges: action.payload
                    }
                };
            case "UPDATE_TRANSACTION_CHARGE_ERROR":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        transactionCharges: {
                            ...state.errors.transactionCharges,
                            [action.payload.field]: action.payload.value,
                        },
                    }
                };
            case "UPDATE_TRANSACTION_CHARGE_ERRORS":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        transactionCharges: action.payload
                    }
                };

            case "UPDATE_MODIFICATION_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        modificationCharges: {
                            ...state.details.modificationCharges,
                            [action.payload.field]: action.payload.value,
                        },
                    },
                    errors: {
                        ...state.errors,
                        modificationCharges: {
                            ...state.errors.modificationCharges,
                            [action.payload.field]: undefined,
                        },
                    }
                };
            case "SET_MODIFICATION_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        modificationCharges: action.payload
                    }
                };
            case "UPDATE_MODIFICATION_CHARGE_ERROR":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        modificationCharges: {
                            ...state.errors.modificationCharges,
                            [action.payload.field]: action.payload.value,
                        }
                    }
                };
            case "UPDATE_MODIFICATION_CHARGE_ERRORS":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        modificationCharges: action.payload
                    }
                };

            case "UPDATE_CREDIT_CARD_OR_IVR_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        creditCardOrIVRCharges: {
                            ...state.details.creditCardOrIVRCharges,
                            [action.payload.field]: action.payload.value,
                        }
                    },
                    errors: {
                        ...state.errors,
                        creditCardOrIVRCharges: {
                            ...state.errors.creditCardOrIVRCharges,
                            [action.payload.field]: undefined,
                        }
                    }
                };
            case "SET_CREDIT_CARD_OR_IVR_CHARGES":
                return {
                    ...state,
                    details: {
                        ...state.details,
                        creditCardOrIVRCharges: action.payload
                    }
                }
            case "UPDATE_CREDIT_CARD_OR_IVR_CHARGE_ERROR":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        creditCardOrIVRCharges: {
                            ...state.errors.creditCardOrIVRCharges,
                            [action.payload.field]: action.payload.value,
                        }
                    }
                };
            case "UPDATE_CREDIT_CARD_OR_IVR_CHARGE_ERRORS":
                return {
                    ...state,
                    errors: {
                        ...state.errors,
                        creditCardOrIVRCharges: action.payload
                    }
                };

            case "RESET_ALL_CHARGES":
                return {
                    ...state,
                    details: {
                        cancellationCharges: {
                            affiliateDomesticCharge: 0,
                            domesticChargeApplyFor: "PER_BOOKING",
                            affiliateInternationalCharge: 0,
                            internationalChargeApplyFor: "PER_BOOKING",
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN"
                        },
                        transactionCharges: {
                            affiliateDomesticCharge: 0,
                            affiliateInternationalCharge: 0,
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN"
                        },
                        modificationCharges: {
                            affiliateDomesticCharge: 0,
                            affiliateInternationalCharge: 0,
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN"
                        },
                        creditCardOrIVRCharges: {
                            affiliateDomesticCharge: 0,
                            affiliateInternationalCharge: 0,
                            customerDomesticCharge: 0,
                            customerInternationalCharge: 0,
                            userType: "ADMIN",
                            affiliateDomesticChargeApplyFor: "PER_BOOKING",
                            affiliateDomesticChargeType: "FIXED",
                            affiliateInternationalChargeApplyFor: "PER_BOOKING",
                            affiliateInternationalChargeType: "FIXED",
                            customerDomesticChargeApplyFor: "PER_BOOKING",
                            customerDomesticChargeType: "FIXED",
                            customerInternationalChargeApplyFor: "PER_BOOKING",
                            customerInternationalChargeType: "FIXED"
                        },
                    }
                };

            default:
                return state;
        };
    };

    const [myCharges, dispatchMyCharges] = useReducer(myChargesReducer, initialState);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isLoadingCancellationCharges, setIsLoadingCancellationCharges] = useState<boolean>(false);
    const [isLoadingTransactionCharges, setIsLoadingTransactionCharges] = useState<boolean>(false);
    const [isLoadingModificationCharges, setIsLoadingModificationCharges] = useState<boolean>(false);
    const [isLoadingCreditCardOrIVRCharges, setIsLoadingCreditCardOrIVRCharges] = useState<boolean>(false);


    useEffect(() => {
        getServiceTypes();
        getAllFeeTypes();
        getBookingChargeCategories();

        getCancellationCharges();
        getTransactionCharges();
        getModificationCharges();
        getCreditCardOrIVRCharges();
    }, []);

    useEffect(() => {
        getCancellationCharges();
        getTransactionCharges();
        getModificationCharges();
        getCreditCardOrIVRCharges();
    }, [myCharges.service]);


    // HANDLER FUNCTIONS
    const validateMyCharges = (type: keyof IMyCharges["details"]) => {
        const errors: ICommonMyChargeError = {
            affiliateDomesticCharge: (
                (
                    myCharges.details[type].affiliateDomesticCharge === null
                    ||
                    myCharges.details[type].affiliateDomesticCharge === undefined
                )
                &&
                myCharges.rules[type].affiliateDomesticCharge === "YES"
            ) ? "Affiliate domestic charge is mandatory" : undefined,
            affiliateInternationalCharge: (
                (
                    myCharges.details[type].affiliateInternationalCharge === null
                    ||
                    myCharges.details[type].affiliateInternationalCharge === undefined
                )
                &&
                myCharges.rules[type].affiliateInternationalCharge === "YES"
            ) ? "Affiliate international charge is mandatory" : undefined,
            customerDomesticCharge: (
                (
                    myCharges.details[type].customerDomesticCharge === null
                    ||
                    myCharges.details[type].customerDomesticCharge === undefined
                )
                &&
                myCharges.rules[type].customerDomesticCharge === "YES"
            ) ? "Customer domestic charge is mandatory" : undefined,
            customerInternationalCharge: (
                (
                    myCharges.details[type].customerInternationalCharge === null
                    ||
                    myCharges.details[type].customerInternationalCharge === undefined
                )
                &&
                myCharges.rules[type].customerInternationalCharge === "YES"
            ) ? "Customer international charge is mandatory" : undefined,
            userType: undefined
            // (
            //     (
            //         myCharges.details[type].userType === null
            //         ||
            //         myCharges.details[type].userType === undefined
            //     )
            //     &&
            //     myCharges.rules[type].userType === "YES"
            // ) ? "User type is mandatory" : undefined
        };

        dispatchMyCharges({
            action:
                type === "cancellationCharges"
                    ?
                    "UPDATE_CANCELLATION_CHARGE_ERRORS"
                    :
                    type === "transactionCharges"
                        ?
                        "UPDATE_TRANSACTION_CHARGE_ERRORS"
                        :
                        type === "modificationCharges"
                            ?
                            "UPDATE_MODIFICATION_CHARGE_ERRORS"
                            :
                            "UPDATE_CREDIT_CARD_OR_IVR_CHARGE_ERRORS",
            payload: errors
        });

        let isValid = Object.values(flattenObject(errors)).every(val => !val);

        // console.log("errors: ", errors);
        // console.log("isValid: ", isValid);

        return isValid;
    };


    // API CALLS
    const getCancellationCharges = async () => {
        setIsLoadingCancellationCharges(true);

        const response = await getData(`my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/cancellation-charges?userType=${getRole()?.[0]}`);

        if (response.isSuccess) {
            // console.log("Response while fetching cancellation charges: ", response.data.data);
            dispatchMyCharges({
                action: "SET_CANCELLATION_CHARGES",
                payload: response.data.data
            });
        } else {
            console.log("Error while fetching cancellation charges: ", response);
            if (response.status !== 404) {
                showToast({
                    type: "error",
                    message: response.message || "Error while fetching cancellation charges",
                });
            };
        };

        setIsLoadingCancellationCharges(false);
    };

    const saveCancellationCharges = async () => {
        const isValid = validateMyCharges("cancellationCharges");

        if (!isValid) return;

        setIsSaving(true);

        let url = `my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/cancellation-charges`;
        let queryParams = `?affiliateDomesticCharge=${myCharges.details.cancellationCharges.affiliateDomesticCharge}&affiliateInternationalCharge=${myCharges.details.cancellationCharges.affiliateInternationalCharge}&customerDomesticCharge=${myCharges.details.cancellationCharges.customerDomesticCharge}&customerInternationalCharge=${myCharges.details.cancellationCharges.customerInternationalCharge}&userType=${getRole()?.[0]}`;
        console.log("Payload: ",myCharges.details.cancellationCharges);
        const response = await postData(
            url + queryParams,
            null
        );
        if (response.isSuccess) {
            showToast({
                type: "success",
                message: "Cancellation charges saved successfully",
            });
            dispatchMyCharges({
                action: "SET_CANCELLATION_CHARGES",
                payload: response.data.data
            });
        } else {
            console.log("Error while saving cancellation charges: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving cancellation charges",
            });
        };

        setIsSaving(false);
    };

    const getTransactionCharges = async () => {
        setIsLoadingTransactionCharges(true);

        const response = await getData(`my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/transaction-charges?userType=${getRole()?.[0]}`);

        if (response.isSuccess) {
            // console.log("Response while fetching transaction charges: ", response.data.data);
            dispatchMyCharges({
                action: "SET_TRANSACTION_CHARGES",
                payload: response.data.data
            });
        } else {
            console.log("Error while fetching transaction charges: ", response);
            if (response.status !== 404) {
                showToast({
                    type: "error",
                    message: response.message || "Error while fetching transaction charges",
                });
            };
        };

        setIsLoadingTransactionCharges(false);
    };

    const saveTransactionCharges = async () => {
        const isValid = validateMyCharges("transactionCharges");

        if (!isValid) return;

        setIsSaving(true);

        let url = `my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/transaction-charges`;
        let queryParams = `?affiliateDomesticCharge=${myCharges.details.transactionCharges.affiliateDomesticCharge}&affiliateInternationalCharge=${myCharges.details.transactionCharges.affiliateInternationalCharge}&customerDomesticCharge=${myCharges.details.transactionCharges.customerDomesticCharge}&customerInternationalCharge=${myCharges.details.transactionCharges.customerInternationalCharge}&userType=${getRole()?.[0]}`;

        const response = await postData(
            url + queryParams,
            null
        );

        if (response.isSuccess) {
            // console.log("Response while saving transaction charges: ", response.data.data);
            showToast({
                type: "success",
                message: "Transaction charges saved successfully",
            });
            dispatchMyCharges({
                action: "SET_CANCELLATION_CHARGES",
                payload: response.data.data
            });
        } else {
            console.log("Error while saving transaction charges: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving transaction charges",
            });
        };

        setIsSaving(false);
    };

    const getModificationCharges = async () => {
        setIsLoadingModificationCharges(true);

        const response = await getData(`my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/modification-charges?userType=${getRole()?.[0]}`);

        if (response.isSuccess) {
            // console.log("Response while fetching modification charges: ", response.data.data);
            dispatchMyCharges({
                action: "SET_MODIFICATION_CHARGES",
                payload: response.data.data
            });
        } else {
            console.log("Error while fetching modification charges: ", response);
            if (response.status !== 404) {
                showToast({
                    type: "error",
                    message: response.message || "Error while fetching modification charges",
                });
            };
        };

        setIsLoadingModificationCharges(false);
    };

    const saveModificationCharges = async () => {
        const isValid = validateMyCharges("modificationCharges");

        if (!isValid) return;

        setIsSaving(true);

        let url = `my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/modification-charges`;
        let queryParams = `?affiliateDomesticCharge=${myCharges.details.modificationCharges.affiliateDomesticCharge}&affiliateInternationalCharge=${myCharges.details.modificationCharges.affiliateInternationalCharge}&customerDomesticCharge=${myCharges.details.modificationCharges.customerDomesticCharge}&customerInternationalCharge=${myCharges.details.modificationCharges.customerInternationalCharge}&userType=${getRole()?.[0]}`;

        const response = await postData(
            url + queryParams,
            null
        );

        if (response.isSuccess) {
            // console.log("Response while saving modification charges: ", response.data.data);
            showToast({
                type: "success",
                message: "Transaction charges saved successfully",
            });
            dispatchMyCharges({
                action: "SET_CANCELLATION_CHARGES",
                payload: response.data.data
            });
        } else {
            console.log("Error while saving modification charges: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving modification charges",
            });
        };

        setIsSaving(false);
    };

    const getCreditCardOrIVRCharges = async () => {
        setIsLoadingCreditCardOrIVRCharges(true);

        const response = await getData(`my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/credit-card-or-ivr-charges?userType=${getRole()?.[0]}`);

        if (response.isSuccess) {
            // console.log("Response while fetching credit-card-or-ivr charges: ", response.data.data);
            dispatchMyCharges({
                action: "SET_CREDIT_CARD_OR_IVR_CHARGES",
                payload: {
                    ...response.data.data,
                    ...response.data.data?.extraDetails
                }
            });
        } else {
            console.log("Error while fetching credit-card-or-ivr charges: ", response);
            if (response.status !== 404) {
                showToast({
                    type: "error",
                    message: response.message || "Error while fetching credit-card-or-ivr charges",
                });
            };
        };

        setIsLoadingCreditCardOrIVRCharges(false);
    };

    const saveCreditCardOrIVRCharges = async () => {
        const isValid = validateMyCharges("creditCardOrIVRCharges");

        if (!isValid) return;

        setIsSaving(true);

        let url = `my-charges/${myCharges.service === "FLIGHT" ? "flights" : "hotels"}/credit-card-or-ivr-charges`;
        let queryParams = `?affiliateDomesticCharge=${myCharges.details.creditCardOrIVRCharges.affiliateDomesticCharge}&affiliateInternationalCharge=${myCharges.details.creditCardOrIVRCharges.affiliateInternationalCharge}&customerDomesticCharge=${myCharges.details.creditCardOrIVRCharges.customerDomesticCharge}&customerInternationalCharge=${myCharges.details.creditCardOrIVRCharges.customerInternationalCharge}&userType=${getRole()?.[0]}&affiliateDomesticChargeApplyFor=${myCharges.details.creditCardOrIVRCharges.affiliateDomesticChargeApplyFor}&affiliateDomesticChargeType=${myCharges.details.creditCardOrIVRCharges.affiliateDomesticChargeType}&affiliateInternationalChargeApplyFor=${myCharges.details.creditCardOrIVRCharges.affiliateInternationalChargeApplyFor}&affiliateInternationalChargeType=${myCharges.details.creditCardOrIVRCharges.affiliateInternationalChargeType}&customerDomesticChargeApplyFor=${myCharges.details.creditCardOrIVRCharges.customerDomesticChargeApplyFor}&customerDomesticChargeType=${myCharges.details.creditCardOrIVRCharges.customerDomesticChargeType}&customerInternationalChargeApplyFor=${myCharges.details.creditCardOrIVRCharges.customerInternationalChargeApplyFor}&customerInternationalChargeType=${myCharges.details.creditCardOrIVRCharges.customerInternationalChargeType}`;

        const response = await postData(
            url + queryParams,
            null
        );

        if (response.isSuccess) {
            // console.log("Response while saving credit-card-or-ivr-charges charges: ", response.data.data);
            showToast({
                type: "success",
                message: "Transaction charges saved successfully",
            });
            dispatchMyCharges({
                action: "SET_CANCELLATION_CHARGES",
                payload: {
                    ...response.data.data,
                    ...response.data.data?.extraDetails
                }
            });
        } else {
            console.log("Error while saving credit-card-or-ivr-charges charges: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving credit-card-or-ivr-charges charges",
            });
        };

        setIsSaving(false);
    };


    return {
        allFeeTypes,
        serviceTypes,
        applyForTypes,
        bookingChargeCategories,
        // getServiceTypes
        myCharges,
        dispatchMyCharges,
        isSaving,
        saveCancellationCharges,
        isLoadingCancellationCharges,
        saveTransactionCharges,
        isLoadingTransactionCharges,
        saveModificationCharges,
        isLoadingModificationCharges,
        saveCreditCardOrIVRCharges,
        isLoadingCreditCardOrIVRCharges,
    };
};

export default useMyCharges;


const initialState: IMyCharges = {
    service: "FLIGHT",
    details: {
        cancellationCharges: {
            affiliateDomesticCharge: 0,
            affiliateInternationalCharge: 0,
            affiliateDomesticChargeApplyFor: "PER_BOOKING",
            affiliateInternationalChargeApplyFor: "PER_BOOKING",
            customerDomesticCharge: 0,
            customerInternationalCharge: 0,
            userType: "ADMIN"
        },
        transactionCharges: {
            affiliateDomesticCharge: 0,
            affiliateInternationalCharge: 0,
            customerDomesticCharge: 0,
            customerInternationalCharge: 0,
            userType: "ADMIN"
        },
        modificationCharges: {
            affiliateDomesticCharge: 0,
            affiliateInternationalCharge: 0,
            customerDomesticCharge: 0,
            customerInternationalCharge: 0,
            userType: "ADMIN"
        },
        creditCardOrIVRCharges: {
            affiliateDomesticCharge: 0,
            affiliateInternationalCharge: 0,
            customerDomesticCharge: 0,
            customerInternationalCharge: 0,
            userType: "ADMIN",
            affiliateDomesticChargeApplyFor: "PER_BOOKING",
            affiliateDomesticChargeType: "FIXED",
            affiliateInternationalChargeApplyFor: "PER_BOOKING",
            affiliateInternationalChargeType: "FIXED",
            customerDomesticChargeApplyFor: "PER_BOOKING",
            customerDomesticChargeType: "FIXED",
            customerInternationalChargeApplyFor: "PER_BOOKING",
            customerInternationalChargeType: "FIXED"
        },
    },
    rules: {
        cancellationCharges: {
            affiliateDomesticCharge: "YES",
            affiliateInternationalCharge: "YES",
            customerDomesticCharge: "YES",
            customerInternationalCharge: "YES",
            userType: "YES"
        },
        transactionCharges: {
            affiliateDomesticCharge: "YES",
            affiliateInternationalCharge: "YES",
            customerDomesticCharge: "YES",
            customerInternationalCharge: "YES",
            userType: "YES"
        },
        modificationCharges: {
            affiliateDomesticCharge: "YES",
            affiliateInternationalCharge: "YES",
            customerDomesticCharge: "YES",
            customerInternationalCharge: "YES",
            userType: "YES"
        },
        creditCardOrIVRCharges: {
            affiliateDomesticCharge: "YES",
            affiliateInternationalCharge: "YES",
            customerDomesticCharge: "YES",
            customerInternationalCharge: "YES",
            userType: "YES"
        },
    },
    errors: {
        cancellationCharges: {
            affiliateDomesticCharge: undefined,
            affiliateInternationalCharge: undefined,
            customerDomesticCharge: undefined,
            customerInternationalCharge: undefined,
            userType: undefined
        },
        transactionCharges: {
            affiliateDomesticCharge: undefined,
            affiliateInternationalCharge: undefined,
            customerDomesticCharge: undefined,
            customerInternationalCharge: undefined,
            userType: undefined
        },
        modificationCharges: {
            affiliateDomesticCharge: undefined,
            affiliateInternationalCharge: undefined,
            customerDomesticCharge: undefined,
            customerInternationalCharge: undefined,
            userType: undefined
        },
        creditCardOrIVRCharges: {
            affiliateDomesticCharge: undefined,
            affiliateInternationalCharge: undefined,
            customerDomesticCharge: undefined,
            customerInternationalCharge: undefined,
            userType: undefined
        },
    }
};